import { useNavigate } from 'react-router-dom';

import { Badge } from '@/design-system/primitives';
import { STAGE_FILTERS, useDeleteView, useSupportViews } from '@/lib/api/ticketHooks';

/**
 * Saved views — personal + shared (Linear-style). A view is a named filter
 * set + view mode; opening one lands on the board with those filters applied
 * via URL params. Only the owner can delete their views.
 */
export function SupportViewsScreen() {
  const { data, isLoading, error } = useSupportViews();
  const del = useDeleteView();
  const navigate = useNavigate();

  const openView = (filters: Record<string, string>) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) if (v) params.set(k, v);
    navigate(`/admin/support?${params.toString()}`);
  };

  const filterSummary = (filters: Record<string, string>) => {
    const parts: string[] = [];
    if (filters.q) parts.push(`"${filters.q}"`);
    if (filters.stage)
      parts.push(STAGE_FILTERS.find((s) => s.key === filters.stage)?.label ?? filters.stage);
    if (filters.assignee)
      parts.push(filters.assignee === 'me' ? 'My tasks' : filters.assignee === 'unassigned' ? 'Unassigned' : 'Assignee');
    if (filters.disposition) parts.push(filters.disposition);
    if (filters.overdue) parts.push('overdue');
    if (filters.from || filters.to) parts.push(`${filters.from || '…'} → ${filters.to || '…'}`);
    return parts.length ? parts.join(' · ') : 'No filters (all open tickets)';
  };

  return (
    <div className="flex flex-col gap-20">
      <div>
        <h1 className="type-headline-md">Views</h1>
        <p className="type-label-sm text-ink-muted mt-4">
          Saved filter sets for the Support board. Save one from the board&apos;s filter bar
          (&quot;Save view&quot;) — personal views are only visible to you; shared views appear
          here for every admin.
        </p>
      </div>

      {isLoading ? (
        <div className="type-label-sm text-ink-muted py-40">Loading views…</div>
      ) : error ? (
        <div className="type-label-sm text-danger py-40">Failed to load views.</div>
      ) : (data?.views ?? []).length === 0 ? (
        <div className="border border-line rounded-lg bg-raised p-20 type-label-sm text-ink-muted">
          No saved views yet. Apply filters on the board, then hit &quot;Save view&quot;.
        </div>
      ) : (
        <div className="border border-line rounded-lg bg-raised overflow-hidden">
          <table className="w-full text-left type-label-sm [&_th]:px-16 [&_th]:py-10 [&_td]:px-16 [&_td]:py-10">
            <thead className="text-10 uppercase text-ink-muted bg-sunken/50">
              <tr>
                <th>Name</th>
                <th>Visibility</th>
                <th>Owner</th>
                <th className="w-[8rem]">View</th>
                <th className="w-[6rem]" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {(data?.views ?? []).map((v) => (
                <tr
                  key={v.id}
                  onClick={() => openView(v.filters ?? {})}
                  className="border-t border-line hover:bg-sunken/40 cursor-pointer"
                >
                  <td>
                    <div className="font-semibold">{v.name}</div>
                    <div className="text-10 text-ink-muted">{filterSummary(v.filters ?? {})}</div>
                  </td>
                  <td>
                    <Badge tone={v.shared ? 'info' : 'neutral'} size="sm">
                      {v.shared ? 'shared' : 'personal'}
                    </Badge>
                  </td>
                  <td className="whitespace-nowrap">{v.owner_name ?? '—'}</td>
                  <td className="capitalize">{v.view_mode}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {data?.viewer_id === v.owner_id && (
                      <button
                        type="button"
                        onClick={() => del.mutate(v.id)}
                        disabled={del.isPending}
                        className="type-label-sm text-danger hover:underline disabled:opacity-50"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
